<script setup>
import { h, ref, computed, onMounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import DashboardAudioNotificationHelper from 'dashboard/helper/AudioAlerts/DashboardAudioNotificationHelper';
import { showBadgeOnFavicon } from 'dashboard/helper/AudioAlerts/faviconHelper';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import GestaoSidebar from './GestaoSidebar.vue';
import GestaoContent from './GestaoContent.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

// KISOFT: modo Gestao. Quando ligado, a barra da Gestao substitui a do Chatwoot e o
// conteudo da direita mostra a pagina ativa (kisoftView) ou, se 'atendimento', as conversas.
// Padrao LIGADO: a pagina ja abre com a barra da Gestao + as conversas (Atendimento).
// LINK: abrir a URL com ?cw=1 entra no Chatwoot NORMAL (barra original) sem opcao na sidebar;
// fica valendo na aba (sessionStorage) ate clicar em Gestao. ?cw=0 volta pro modo Gestao.
const kisoftNative = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get('cw');
    if (q === '1') { sessionStorage.setItem('kisoft_cw_native', '1'); return true; }
    if (q === '0') { sessionStorage.removeItem('kisoft_cw_native'); return false; }
    return sessionStorage.getItem('kisoft_cw_native') === '1';
  } catch (e) { return false; }
})();
const kisoftGestao = ref(!kisoftNative);
const kisoftView = ref('atendimento');
const onGestaoSelect = it => { kisoftView.value = it.view; };
const kisoftEnter = () => { try { sessionStorage.removeItem('kisoft_cw_native'); } catch (e) {} kisoftGestao.value = true; kisoftView.value = 'home'; };
const kisoftExit = () => { try { sessionStorage.setItem('kisoft_cw_native', '1'); } catch (e) {} kisoftGestao.value = false; };
// KISOFT: barra da Gestao RECOLHIVEL (so icones, 64px). A escolha fica salva neste navegador.
const kisoftRecolhida = ref((() => { try { return localStorage.getItem('kisoft_gs_recolhida') === '1'; } catch (e) { return false; } })());
const kisoftToggleRecolher = () => { kisoftRecolhida.value = !kisoftRecolhida.value; try { localStorage.setItem('kisoft_gs_recolhida', kisoftRecolhida.value ? '1' : '0'); } catch (e) {} };
// KISOFT: NAO fixada = so icones. ABRE ao passar o mouse no ICONE DE ABERTURA (topo) e FECHA quando o mouse
// SAI da barra (largura animada). Fixada (pin) = fica aberta. Encostar no resto da barra NAO abre.
// Atrasos: 120ms p/ abrir (mouse passando rapido nao abre) e 280ms p/ fechar (nao pisca ao sair e voltar).
const kisoftHover = ref(false);
let kisoftHoverT = null;
const kisoftExpandida = computed(() => !kisoftRecolhida.value || kisoftHover.value);
const kisoftAbrirHover = () => { clearTimeout(kisoftHoverT); kisoftHoverT = setTimeout(() => { kisoftHover.value = true; }, 120); };
const kisoftRailEnter = () => { if (kisoftHover.value) clearTimeout(kisoftHoverT); };
const kisoftRailLeave = () => { clearTimeout(kisoftHoverT); kisoftHoverT = setTimeout(() => { kisoftHover.value = false; }, 280); };

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

// KISOFT: botao 'Equipe' (chat em grupo interno, caixa 15) + destaque de msg nova
const EQUIPE_INBOX_ID = 15;
const kisoftRoute = useRoute();
const equipeConvId = ref(12901);
const equipeTo = computed(() =>
  accountScopedRoute('conversation_through_inbox', {
    inbox_id: EQUIPE_INBOX_ID,
    conversation_id: equipeConvId.value,
  })
);
const isOnEquipe = computed(
  () =>
    (kisoftRoute.name === 'inbox_dashboard' ||
      kisoftRoute.name === 'conversation_through_inbox') &&
    String(kisoftRoute.params.inbox_id) === String(EQUIPE_INBOX_ID)
);
const equipeLast = ref(0);
const equipePrevLast = ref(null);
const equipeSeen = ref(Number(localStorage.getItem('kisoft_equipe_seen') || 0));
const equipeHasNew = computed(
  () => !isOnEquipe.value && equipeLast.value > equipeSeen.value
);
const kisoftEquipeTitle = on => {
  try {
    const base = document.title.replace(/^\uD83D\uDD34\s+/, '');
    document.title = on ? '\uD83D\uDD34 ' + base : base;
  } catch (e) {
    /* noop */
  }
};
const markEquipeSeen = () => {
  equipeSeen.value = Math.floor(Date.now() / 1000);
  localStorage.setItem('kisoft_equipe_seen', String(equipeSeen.value));
  kisoftEquipeTitle(false);
  kisoftStopAlertLoop();
};
const kisoftCurrentUser = useMapGetter('getCurrentUser');
let kisoftAlertTimer = null;
const kisoftPlaySound = () => {
  try {
    if (!DashboardAudioNotificationHelper.audioConfig.audio)
      DashboardAudioNotificationHelper.intializeAudio();
    DashboardAudioNotificationHelper.playAudioAlert();
  } catch (e) {
    /* noop */
  }
};
const kisoftStopAlertLoop = () => {
  if (kisoftAlertTimer) {
    clearInterval(kisoftAlertTimer);
    kisoftAlertTimer = null;
  }
};
const kisoftStartAlertLoop = () => {
  if (kisoftAlertTimer) return;
  kisoftAlertTimer = setInterval(() => {
    if (!equipeHasNew.value || isOnEquipe.value) {
      kisoftStopAlertLoop();
      return;
    }
    kisoftPlaySound();
    try {
      showBadgeOnFavicon();
    } catch (e) {
      /* noop */
    }
  }, 12000);
};
const kisoftNotifyEquipe = (nome, texto) => {
  kisoftPlaySound();
  try {
    if (window.Notification && window.Notification.permission === 'granted') {
      const n = new window.Notification(nome || 'Equipe', {
        body: texto || 'Nova mensagem no chat da equipe',
        tag: 'kisoft-equipe',
        renotify: true,
        requireInteraction: true,
        icon: '/favicon-badge-32.png',
      });
      n.onclick = () => {
        window.focus();
      };
    }
  } catch (e) {
    /* noop */
  }
  try {
    showBadgeOnFavicon();
  } catch (e) {
    /* noop */
  }
  kisoftEquipeTitle(true);
  kisoftStartAlertLoop();
};
const refreshEquipe = async () => {
  try {
    const res = await ConversationAPI.get({
      inboxId: EQUIPE_INBOX_ID,
      status: 'all',
      assigneeType: 'all',
    });
    const convs =
      (res && res.data && res.data.data && res.data.data.payload) || [];
    let top = null;
    convs.forEach(c => {
      if (
        !top ||
        (Number(c.last_activity_at) || 0) > (Number(top.last_activity_at) || 0)
      )
        top = c;
    });
    if (top && top.id) equipeConvId.value = top.id;
    const newLast = top ? Number(top.last_activity_at) || 0 : 0;
    const lm = top && top.last_non_activity_message;
    const senderId = lm ? lm.sender_id || (lm.sender && lm.sender.id) : null;
    const myId = kisoftCurrentUser.value && kisoftCurrentUser.value.id;
    const isMine = !!senderId && !!myId && senderId === myId;
    if (equipePrevLast.value !== null && newLast > equipePrevLast.value) {
      if (isMine) {
        // minha propria mensagem -> marca como vista, nao notifica
        equipeSeen.value = newLast;
        localStorage.setItem('kisoft_equipe_seen', String(newLast));
        kisoftStopAlertLoop();
        kisoftEquipeTitle(false);
      } else if (newLast > equipeSeen.value && !isOnEquipe.value) {
        const nome = (lm && lm.sender && lm.sender.name) || 'Equipe';
        const texto = (lm && lm.content) || 'Nova mensagem';
        kisoftNotifyEquipe(nome, texto);
      }
    }
    equipePrevLast.value = newLast;
    equipeLast.value = newLast;
    if (isOnEquipe.value) markEquipeSeen();
  } catch (e) {
    /* noop */
  }
};
watch(isOnEquipe, v => {
  if (v) markEquipeSeen();
});
onMounted(() => {
  try {
    if (window.Notification && window.Notification.permission === 'default')
      window.Notification.requestPermission();
  } catch (e) {
    /* noop */
  }
  refreshEquipe();
  setInterval(refreshEquipe, 15000);
});

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  const _kisoftHide = ['Conversation', 'Captain', 'Campaigns', 'Portals'];
  return [
    {
      name: 'Inbox',
      label: 'Todas as conversas',
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('home'),
      activeOn: ['home', 'inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Caixas',
      label: 'Caixas de entrada',
      icon: 'i-lucide-mailbox',
      activeOn: ['conversation_through_inbox'],
      children: sortedInboxes.value
        .filter(inbox => Number(inbox.id) !== 15)
        .map(inbox => ({
          name: `caixa-${inbox.id}`,
          label: inbox.name,
          icon: h(ChannelIcon, { inbox, class: 'size-[12px]' }),
          to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
          component: leafProps =>
            h(ChannelLeaf, {
              label: leafProps.label,
              active: leafProps.active,
              inbox,
            }),
        })),
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            icon: h(ChannelIcon, { inbox, class: 'size-[12px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Captain',
      icon: 'i-woot-captain',
      label: t('SIDEBAR.CAPTAIN'),
      activeOn: ['captain_assistants_create_index'],
      children: [
        {
          name: 'FAQs',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          activeOn: [
            'captain_assistants_responses_index',
            'captain_assistants_responses_pending',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_responses_index',
          }),
        },
        {
          name: 'Documents',
          label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
          activeOn: ['captain_assistants_documents_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_documents_index',
          }),
        },
        {
          name: 'Scenarios',
          label: t('SIDEBAR.CAPTAIN_SCENARIOS'),
          activeOn: ['captain_assistants_scenarios_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_scenarios_index',
          }),
        },
        {
          name: 'Playground',
          label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
          activeOn: ['captain_assistants_playground_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_playground_index',
          }),
        },
        {
          name: 'Inboxes',
          label: t('SIDEBAR.CAPTAIN_INBOXES'),
          activeOn: ['captain_assistants_inboxes_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_inboxes_index',
          }),
        },
        {
          name: 'Tools',
          label: t('SIDEBAR.CAPTAIN_TOOLS'),
          activeOn: ['captain_tools_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_tools_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.CAPTAIN_SETTINGS'),
          activeOn: [
            'captain_assistants_settings_index',
            'captain_assistants_guidelines_index',
            'captain_assistants_guardrails_index',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ].filter(i => !_kisoftHide.includes(i.name));
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-[#dbeafe] dark:bg-[#1e3a8a] flex flex-col text-sm pb-0.5 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: (kisoftGestao ? (kisoftExpandida ? 220 : 64) : sidebarWidth) + 'px', transition: kisoftGestao ? 'width .22s ease' : undefined }"
  >
    <template v-if="!kisoftGestao">
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <button
            type="button"
            class="grid place-content-center rounded-lg size-8 hover:bg-n-alpha-2"
            @click="onResizeHandleDoubleClick"
          >
            <Logo class="size-5" />
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="grid flex-shrink-0 place-content-center size-6"
            @click="onResizeHandleDoubleClick"
          >
            <Logo class="size-4" />
          </button>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle, isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <!-- KISOFT: botao Gestao (entra no modo Gestao) -->
        <li class="grid text-sm cursor-pointer select-none min-w-0">
          <button
            type="button"
            title="Gestao"
            class="flex items-center gap-2 rounded-lg h-8 min-w-0 w-full font-medium text-[#f2662e] hover:bg-n-alpha-2"
            :class="isEffectivelyCollapsed ? 'justify-center size-10' : 'px-1.5 py-1'"
            @click="kisoftEnter"
          >
            <span class="i-lucide-layout-grid size-4" />
            <span v-if="!isEffectivelyCollapsed" class="truncate flex-grow text-start"
              >Gestão</span
            >
          </button>
        </li>
        <template v-for="item in menuItems" :key="item.name">
          <SidebarGroup v-bind="item" />
          <!-- KISOFT: botao Equipe logo apos 'Todas as conversas' -->
          <li
            v-if="item.name === 'Inbox'"
            class="grid text-sm cursor-pointer select-none min-w-0"
          >
            <router-link
              :to="equipeTo"
              title="Equipe"
              class="flex items-center gap-2 rounded-lg h-8 min-w-0"
              :class="[
                isEffectivelyCollapsed ? 'justify-center size-10' : 'px-1.5 py-1',
                isOnEquipe
                  ? 'text-n-slate-12 bg-n-alpha-2 font-medium'
                  : equipeHasNew
                    ? 'bg-[#dc2626] text-white font-medium'
                    : 'text-n-slate-11 hover:bg-n-alpha-2',
              ]"
              @click="markEquipeSeen"
            >
              <span class="i-lucide-users-round size-4" />
              <span v-if="!isEffectivelyCollapsed" class="truncate flex-grow"
                >Equipe</span
              >
            </router-link>
          </li>
        </template>
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="p-1 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
    </template>
    <!-- KISOFT: barra da Gestao substitui a do Chatwoot -->
    <div v-else class="kg-rail" @mouseenter="kisoftRailEnter" @mouseleave="kisoftRailLeave">
      <GestaoSidebar :view="kisoftView" :recolhida="!kisoftExpandida" :fixada="!kisoftRecolhida" @select="onGestaoSelect" @exit="kisoftExit" @toggle="kisoftToggleRecolher" @abrir="kisoftAbrirHover" />
    </div>
  </aside>
  <!-- KISOFT: area de conteudo da Gestao (escondida em 'atendimento' -> mostra as conversas) -->
  <Teleport to="body">
    <GestaoContent v-if="kisoftGestao && kisoftView !== 'atendimento'" :view="kisoftView" :recolhida="!kisoftExpandida" />
  </Teleport>
</template>
